import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { SpinnerGap, MagnifyingGlass, UserCirclePlus } from '@phosphor-icons/react';

export default function SearchPage() {
  const navigate = useNavigate();
  const { switchUser } = useAuth();
  const [form, setForm] = useState({ Nome: '', Email: '', ID_Cliente: '', Empresa: '', NIF: '', TiposCliente: '', Particular: '' });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inactive, setInactive] = useState(null); // cliente inativo encontrado p/ reactivar
  const [reactivating, setReactivating] = useState(false);

  const openFicha = async (id) => {
    await switchUser(id);
    navigate('/perfil');
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setInactive(null);
    try {
      const r = await api.post('/pesquisa/advanced', form);
      const list = Array.isArray(r.data) ? r.data : [];
      setResults(r.data?.message ? r.data.message : list);
      // Sem resultados mas pesquisou por email → ver se existe inativo na rede.
      if ((r.data?.message || list.length === 0) && form.Email.trim()) {
        try {
          const ri = await api.post('/pesquisa/inativo-por-email', { Email: form.Email.trim() });
          if (Array.isArray(ri.data) && ri.data.length > 0) setInactive(ri.data[0]);
        } catch { /* noop */ }
      }
    } catch { setResults('Erro na pesquisa'); }
    setLoading(false);
  };

  const confirmReactivar = async () => {
    if (!inactive) return;
    setReactivating(true);
    try {
      const r = await api.post('/pesquisa/reactivar', { ID_Cliente: inactive.ID_Cliente });
      if (r.data?.success) {
        await openFicha(inactive.ID_Cliente);
      } else {
        setResults(r.data?.message || 'Não foi possível reactivar o cliente.');
        setInactive(null);
      }
    } catch { setResults('Erro ao reactivar o cliente.'); setInactive(null); }
    setReactivating(false);
  };

  return (
    <div data-testid="search-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Pesquisa Avançada</h1>
        <p className="text-sm text-[#6B7280] mt-1">Pesquisa de clientes na rede (sp_pesquisaClientes)</p>
      </div>

      <form onSubmit={handleSearch} className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {[{k:'Nome',l:'Nome'},{k:'Email',l:'Email'},{k:'ID_Cliente',l:'ID Cliente'},{k:'Empresa',l:'Empresa'},{k:'NIF',l:'NIF'},{k:'TiposCliente',l:'Tipo (CF,CS,CT,AF,PR)'}].map(f => (
            <div key={f.k}>
              <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-1 block">{f.l}</label>
              <input value={form[f.k]} onChange={e => setForm({...form, [f.k]: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829] outline-none" />
            </div>
          ))}
        </div>
        <button type="submit" disabled={loading} className="px-4 py-2 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#002480] flex items-center gap-1.5 disabled:opacity-50">
          {loading ? <SpinnerGap size={14} className="animate-spin" /> : <MagnifyingGlass size={14} />} Pesquisar
        </button>
      </form>

      {inactive && (
        <div data-testid="reactivar-prompt" className="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 grid place-items-center text-amber-600 shrink-0">
              <UserCirclePlus size={22} weight="fill" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#111827]">Cliente desativado na sua rede</p>
              <p className="text-sm text-[#6B7280] mt-1">
                <b className="text-[#111827]">{inactive.NomeCompleto}</b>
                {inactive.Email ? ` (${inactive.Email})` : ''} existe na sua rede mas está <b>desativado</b>.
                Deseja reativá-lo e abrir a ficha?
              </p>
              <div className="flex gap-2 mt-3">
                <button data-testid="reactivar-confirm" onClick={confirmReactivar} disabled={reactivating}
                  className="px-4 py-2 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#002480] flex items-center gap-1.5 disabled:opacity-50">
                  {reactivating ? <SpinnerGap size={14} className="animate-spin" /> : null} Sim, reativar e abrir ficha
                </button>
                <button data-testid="reactivar-cancel" onClick={() => setInactive(null)} disabled={reactivating}
                  className="px-4 py-2 bg-white border border-[#E5E7EB] text-[#6B7280] text-sm font-medium rounded-md hover:bg-[#F9FAFB] disabled:opacity-50">
                  Não
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {typeof results === 'string' && <div className="text-center py-8 text-[#6B7280]">{results}</div>}

      {Array.isArray(results) && results.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          <p className="px-4 py-2 text-sm text-[#6B7280] bg-[#F9FAFB] border-b border-[#E5E7EB]">{results.length} resultados</p>
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">ID</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Nome</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Email</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Tipo</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Empresa</th>
            </tr></thead>
            <tbody>
              {results.slice(0, 100).map((r) => (
                <tr key={`client-${r.ID_Cliente}`} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
                  <td className="px-4 py-3 text-sm font-mono text-[#6B7280]">{r.ID_Cliente}</td>
                  <td className="px-4 py-3 text-sm font-medium text-[#111827]">{r.NomeCompleto}</td>
                  <td className="px-4 py-3 text-sm text-[#6B7280]">{r.Email || '-'}</td>
                  <td className="px-4 py-3"><span className="text-[9px] uppercase tracking-[0.15em] font-semibold px-1.5 py-0.5 rounded border bg-blue-50 text-[#0F1829] border-[#0F1829]/20">{r.TipoCliente || r.ID_TipoCliente}</span></td>
                  <td className="px-4 py-3 text-sm text-[#6B7280]">{r.Empresa || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
